import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import faqLottie from "../assets/lottie-files/faq.lottie";
import { sanityFetch } from "../sanityClient";

type FAQ = {
  question: string;
  answer: string;
  tags: string[];
  link?: string;
};

const allTags = ["All", "Main Market Hall", "Kitchen", "Workshop Room"];

const tagColor = (tag: string) => {
  switch (tag) {
    case "Main Market Hall": return "#6dc744";
    case "Kitchen":          return "#2d6a14";
    case "Workshop Room":    return "#C19A3B";
    default:                 return "#c6252d";
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 800, color: "#0d1b2a", margin: 0 }}>
      {children}
    </h2>
    <div style={{ width: 56, height: 4, background: "linear-gradient(90deg, #6dc744, #4a8c25)", borderRadius: 2, marginTop: 8 }} />
  </div>
);

const FAQs: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedTag, setSelectedTag] = useState<string>("All");
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const faqTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    sanityFetch<FAQ[]>(`*[_type == "faq"] | order(order asc) { question, answer, tags, link }`)
      .then(setFaqs);
  }, []);

  const filteredFaqs =
    selectedTag === "All"
      ? faqs
      : faqs.filter((faq) =>
          faq.tags.some((tag) => tag.toLowerCase() === selectedTag.toLowerCase())
        );

  return (
    <div>
      {/* HERO */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3rem", position: "relative", zIndex: 1 }}>
          <motion.div style={{ flex: "1 1 480px", minWidth: 300 }} variants={stagger} initial="hidden" animate="visible">
            <motion.span variants={fadeUp} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}>
              <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
              Got Questions?
            </motion.span>

            <motion.h1 variants={fadeUp} style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}>
              Frequently Asked{" "}
              <span style={{ background: "linear-gradient(90deg, #6dc744, #c6252d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Questions
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "2rem", maxWidth: 520 }}>
              Got questions about the Market? We've got answers! Browse below to find everything you need to know.
            </motion.p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.88 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.85, ease: "easeOut" }} style={{ flex: "1 1 360px", minWidth: 280, display: "flex", justifyContent: "center" }}>
            <DotLottieReact src={faqLottie} loop autoplay style={{ width: "100%", maxWidth: 440 }} />
          </motion.div>
        </div>
      </section>

      {/* ── STICKY CATEGORY FILTER ── */}
      <div style={{ position: "sticky", top: 65, zIndex: 90, background: "rgba(255,253,248,0.95)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(45,106,20,0.12)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "0.75rem 1.5rem", display: "flex", flexWrap: "wrap", gap: "0.65rem" }}>
          {allTags.map((tag) => {
            const color = tagColor(tag);
            const isActive = selectedTag === tag;
            return (
              <button
                key={tag}
                onClick={() => { setSelectedTag(tag); setOpenIndex(null); faqTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                style={{
                  padding: "0.5rem 1.25rem",
                  background: isActive ? color : "white",
                  color: isActive ? "white" : color,
                  border: `2px solid ${color}`,
                  borderRadius: 999,
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  letterSpacing: "0.03em",
                  transition: "all 0.2s ease",
                  boxShadow: isActive ? `0 4px 14px ${color}44` : "0 1px 4px rgba(0,0,0,0.06)",
                }}
                onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.background = `${color}12`; } }}
                onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.background = "white"; } }}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      {/* FILTER + FAQS */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem 5rem" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div ref={faqTopRef} style={{ scrollMarginTop: 120 }} />
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Browse by Category</SectionHeading>
            </motion.div>

            {/* FAQ accordion */}
            {filteredFaqs.length === 0 ? (
              <motion.p variants={fadeUp} style={{ textAlign: "center", fontSize: "1.1rem", color: "#6b7280" }}>
                No FAQs found for "{selectedTag}".
              </motion.p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {filteredFaqs.map((item, index) => {
                  const isOpen = openIndex === index;
                  const color = tagColor(item.tags?.[0] ?? "All");
                  return (
                    <motion.div
                      key={index}
                      initial="hidden"
                      animate="visible"
                      variants={fadeUp}
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      style={{
                        background: "white",
                        borderRadius: 16,
                        border: `1px solid ${isOpen ? color + "55" : "rgba(45,106,20,0.1)"}`,
                        borderLeft: `4px solid ${color}`,
                        boxShadow: isOpen ? `0 6px 24px ${color}18` : "0 2px 12px rgba(0,0,0,0.05)",
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
                        userSelect: isOpen ? "text" : "none",
                      }}
                    >
                      <div style={{ padding: "1.1rem 1.4rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", flex: 1 }}>
                          {item.tags.length > 0 && (
                            <span style={{ color, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                              {item.tags[0]}
                            </span>
                          )}
                          <span style={{ fontSize: "1rem", fontWeight: 700, color: "#0d1b2a", lineHeight: 1.45 }}>
                            {item.question}
                          </span>
                        </div>
                        <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.25s ease, background 0.2s ease", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                            <path d="M5 7.5L10 12.5L15 7.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      </div>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            key="answer"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.28, ease: "easeInOut" }}
                            style={{ overflow: "hidden" }}
                          >
                            <div style={{ padding: "0 1.4rem 1.25rem", borderTop: `1px solid ${color}22` }}>
                              <p style={{ margin: "0.75rem 0 0", fontSize: "0.975rem", lineHeight: 1.75, color: "#374151", whiteSpace: "pre-line" }}>
                                {item.answer}
                                {item.link && (
                                  <>
                                    {" "}
                                    <a
                                      href={item.link}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ color, fontWeight: 700, textDecoration: "underline" }}
                                    >
                                      Learn more
                                    </a>
                                  </>
                                )}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQs;
